
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, LucideIcon } from "lucide-react";

interface Tool {
  icon: LucideIcon;
  title: string;
  description: string;
  features: string[];
}

interface ToolsShowcaseProps {
  tools: Tool[];
}

const ToolsShowcase = ({ tools }: ToolsShowcaseProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.4 }}
      className="mb-20"
    >
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold mb-4 text-foreground">Everything You Need in One Platform</h2>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          From web development to content creation, doozy provides professional-grade tools 
          that work together seamlessly.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tools.map((tool, index) => (
          <motion.div
            key={tool.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6 + index * 0.1 }}
          >
            <Card className="h-full border-border bg-card hover:bg-accent transition-all duration-300 hover:shadow-lg group">
              <CardContent className="p-6">
                <div className="mb-4">
                  <tool.icon className="h-8 w-8 text-foreground mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-semibold mb-2 text-foreground">{tool.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {tool.description}
                  </p>
                </div>
                <div className="space-y-2">
                  {tool.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-3 w-3 text-foreground flex-shrink-0" />
                      <span className="text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default ToolsShowcase;
